/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import UiInput from 'components/Ui/UiInput'
import CompanyFormLabel from 'pages/Front/Booking/components/CompanyForm/CompanyFormLabel'
import clsx from 'clsx'
import { FieldErrors } from 'react-hook-form'
import useAnlasstypOptions, { defaultAnlasstypen } from '../hooks/useAnlasstypOptions'

interface AnlasstypSelectProps {
    value: string | null
    onChange: (value: string | null) => void
    error?: string
    className?: string
    required?: boolean
    errors?: FieldErrors
}

const AnlasstypSelect: React.FC<AnlasstypSelectProps> = ({
    value,
    onChange,
    error,
    className,
    required,
    errors,
}) => {
    const { t } = useTranslation()
    const groupedOptions = useAnlasstypOptions(t('Other'))
    const normalizedValue = (value ?? '').trim()
    const [customModeActive, setCustomModeActive] = useState(false)

    const selectedOption = useMemo(() => {
        if (normalizedValue && defaultAnlasstypen.includes(normalizedValue)) {
            return normalizedValue
        }
        if (normalizedValue) {
            return 'custom'
        }
        if (customModeActive) {
            return 'custom'
        }
        return null
    }, [normalizedValue, customModeActive])

    const customValue = selectedOption === 'custom' ? normalizedValue : ''

    const handleSelectChange = (val: string | number | null) => {
        const stringVal = val ? String(val) : null
        if (stringVal === 'custom') {
            setCustomModeActive(true)
            onChange('')
            return
        }

        setCustomModeActive(false)
        onChange(stringVal ?? '')
    }

    const handleCustomValueChange = (val: string) => {
        setCustomModeActive(true)
        onChange(val.trim() || '')
    }

    const displayLabel =
        selectedOption && selectedOption !== 'custom'
            ? selectedOption
            : customValue.trim() || normalizedValue || ''

    return (
        <div className={clsx('AnlasstypSelect flex flex-col', className)}>
            <CompanyFormLabel
                label={t('Anlasstyp')}
                name="anlasstyp"
                required={required}
                errors={errors || {}}
            />
            <UiSelectWithGraphics
                label=""
                groupedOptions={groupedOptions}
                selected={selectedOption}
                setSelected={handleSelectChange}
                debounceMs={0}
                title={displayLabel || undefined}
                dropdownMinWidth={320}
                wrapOptionLabels
            />
            {selectedOption === 'custom' && (
                <UiInput
                    placeholder={t('Enter custom Anlasstyp')}
                    value={customValue}
                    onChange={(e) => handleCustomValueChange(e.target.value)}
                    hasError={!!error}
                    autoComplete="one-time-code"
                />
            )}
            {!!error && (
                <span className="text-xs text-red-500 mt-1">{error}</span>
            )}
        </div>
    )
}

export default AnlasstypSelect
